/**
 * Playlists — the club's saved reels.
 *
 * The editor and player are React routes since 2026-09-17
 * (pages/playlists/); every link here stays inside the SPA.
 *
 * TWO KINDS OF REEL, and the difference matters to the person reading the
 * list: a manual one is what somebody put in it, a smart one is a rule
 * re-evaluated against new games. Refresh only means anything for the second,
 * so it only appears there.
 *
 * VISIBILITY IS NOT DECORATION — a private reel is invisible to the rest of
 * the club, so it is a chip on every row rather than something you discover
 * by opening it.
 */
import { Plus, RefreshCw, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError, api } from '../lib/api'
import './admin.css'

type Playlist = {
  id: string
  name: string
  description: string | null
  visibility: 'private' | 'team'
  source: string
  rule: unknown | null
  created_at: string | null
  updated_at: string | null
  item_count: number
  is_owner: boolean
}

const when = (iso: string | null) => (iso ? iso.replace('T', ' ').slice(0, 16) : '—')

/** Standalone it is the Reels page; embedded it is the Reels section of the
 *  Clip Room (the index merged there, Ben 2026-10-23) — same list, same
 *  actions, an h2 instead of the page title. */
export default function Playlists({ embedded = false }: { embedded?: boolean }) {
  const [items, setItems] = useState<Playlist[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [visibility, setVisibility] = useState<'private' | 'team'>('private')

  const load = useCallback(() => {
    api<{ playlists: Playlist[] }>('/api/v1/playlists')
      .then((d) => setItems(d.playlists))
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [])
  useEffect(load, [load])

  const create = async () => {
    setBusy('new')
    try {
      await api('/api/v1/playlists', {
        method: 'POST',
        body: JSON.stringify({ name, visibility }),
      })
      setName(''); setCreating(false); load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(null) }
  }

  const remove = async (p: Playlist) => {
    if (!window.confirm(`Delete “${p.name}” and its ${p.item_count} clip`
      + `${p.item_count === 1 ? '' : 's'}? This cannot be undone.`)) return
    setBusy(p.id)
    try {
      await api(`/api/v1/playlists/${p.id}`, { method: 'DELETE' })
      load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(null) }
  }

  const refresh = async (p: Playlist) => {
    setBusy(p.id)
    try {
      await api(`/api/v1/playlists/${p.id}/refresh`, { method: 'POST' })
      load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(null) }
  }

  // Reels (Ben, 2026-10-23): the rail, the title and the counts below all say
  // reels now — the page already counted in reels while titled Playlists.
  const title = embedded
    ? <h2 className="dash-section__title">Reels</h2>
    : <h1 className="page-title">Reels</h1>
  // Title in every state, so the section does not jump when the list lands.
  if (error && !items) {
    return (<>{title}<p className="error">{error}</p></>)
  }
  if (!items) {
    return (<>{title}<p className="muted">Loading…</p></>)
  }

  return (
    <>
      {title}
      <p className="page-sub">
        {items.length} reel{items.length === 1 ? '' : 's'} — the ones shared
        with your club, plus your own private ones.
      </p>

      <div className="adm-bar">
        <span className="adm-bar__spacer" />
        <button className="adm-btn" onClick={() => setCreating((v) => !v)}>
          <Plus size={13} /> {creating ? 'Cancel' : 'New reel'}
        </button>
      </div>

      {creating && (
        <div className="panel" style={{ marginBottom: 'var(--space-6)' }}>
          <div className="adm-row" style={{ marginBottom: 0 }}>
            <label className="adm-field">
              <span className="t-label">Name</span>
              <input className="adm-input" style={{ minWidth: 300 }}
                     placeholder="High press regains, first half"
                     value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="adm-field">
              <span className="t-label">Visible to</span>
              <select className="adm-select" value={visibility}
                      onChange={(e) => setVisibility(e.target.value as 'private' | 'team')}>
                <option value="private">Only me</option>
                <option value="team">My club</option>
              </select>
            </label>
            <button className="adm-btn" disabled={!name.trim() || busy === 'new'}
                    onClick={create}>Create</button>
          </div>
        </div>
      )}

      {error && <p className="adm-msg adm-msg--err">{error}</p>}

      <table className="adm-table">
        <thead>
          <tr>
            <th>Playlist</th><th>Kind</th><th>Visible to</th>
            <th className="adm-num">Clips</th><th>Updated</th><th />
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id}>
              <td>
                <Link className="adm-primary" to={`/playlists/${p.id}`}>{p.name}</Link>
                {p.description && <div className="muted">{p.description}</div>}
              </td>
              <td>
                <span className="adm-chip">
                  {p.source === 'rule' ? 'smart reel' : p.source === 'api' ? 'from API' : 'manual'}
                </span>
              </td>
              <td>
                <span className={`adm-chip${p.visibility === 'team' ? ' adm-chip--staff' : ''}`}>
                  {p.visibility === 'team' ? 'club' : 'only me'}
                </span>
              </td>
              <td className="adm-num">{p.item_count}</td>
              <td className="adm-when">{when(p.updated_at)}</td>
              <td className="adm-num" style={{ whiteSpace: 'nowrap' }}>
                <Link className="adm-btn adm-btn--quiet"
                      to={`/playlists/${p.id}/play`}>Play</Link>
                {p.source === 'rule' && (
                  <button className="adm-btn adm-btn--quiet"
                          style={{ marginLeft: 4 }}
                          disabled={busy === p.id}
                          title="Re-evaluate this reel's rule against every game"
                          onClick={() => refresh(p)}>
                    <RefreshCw size={12} /> Refresh
                  </button>
                )}
                {p.is_owner && (
                  <button className="adm-btn adm-btn--danger"
                          style={{ marginLeft: 4 }}
                          disabled={busy === p.id}
                          aria-label={`Delete ${p.name}`}
                          title={`Delete ${p.name}`}
                          onClick={() => remove(p)}>
                    <Trash2 size={12} />
                  </button>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={6} className="adm-empty">
              No reels yet. Pick a match below to cut clips in its Clip Lab,
              or create an empty reel here and fill it later.
            </td></tr>
          )}
        </tbody>
      </table>
    </>
  )
}
